import type { QueueStorage } from '@church/data'

/**
 * Durable queue storage in the browser.
 *
 * `memoryStorage` was honest for attendance — a volunteer tapping a roster can
 * see the page and will notice a reload. A check-in kiosk cannot get away with
 * it: it runs unattended for two hours, and a tab that reloads (a crash, an
 * iPad reclaiming memory, somebody's elbow) must not lose the children already
 * through the door. So the queue outlives the page.
 *
 * IndexedDB rather than `localStorage`, for two reasons that both matter on a
 * kiosk. It is asynchronous, so writing the queue during a rush does not block
 * the paint of the next screen; and it is the store browsers evict last, which
 * is where a record of who is in the building belongs.
 *
 * It lives in `apps/web` rather than `@church/data` because `QueueStorage` is a
 * port, and this is a platform's answer to it — the shared package has no DOM
 * lib on purpose, so that the same hooks compile for Vite and for Metro.
 *
 * The whole queue is one record under one key. That looks lazy next to a
 * proper object store with one row per item, but the `QueueStorage` port is
 * read-all/write-all and the queue serialises its own mutations — splitting
 * the rows would buy nothing and would add a second place for the two to
 * disagree. A Sunday's queue is tens of kilobytes.
 */

const DB_NAME = 'church-offline'
const STORE = 'queues'
const VERSION = 1

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('indexedDB open failed'))
  })
}

function run<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode)
    const request = work(transaction.objectStore(STORE))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('indexedDB request failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('indexedDB aborted'))
  })
}

/** True when this browser can actually give us a durable queue. */
export function indexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined'
}

/**
 * A `QueueStorage` backed by IndexedDB under `key`.
 *
 * The database handle is opened once and reused: opening per write would make
 * every check-in wait on a connection, and the whole point of the queue is
 * that a check-in never waits for anything.
 */
export function indexedDbStorage(key: string): QueueStorage {
  let handle: Promise<IDBDatabase> | null = null
  const db = () => (handle ??= openDatabase())

  return {
    async read() {
      const value = await run<unknown>(await db(), 'readonly', (store) => store.get(key))
      return typeof value === 'string' ? value : null
    },
    async write(value) {
      await run(await db(), 'readwrite', (store) => store.put(value, key))
    },
  }
}
